import { z } from 'zod';
import { idSchema } from './common.js';
import { lessonIdSchema, sourceTypeSchema } from './content.js';
import { findingSchema, generationJobIdSchema } from './generation.js';
/**
 * What a verify job records for one lesson — CURR-28.
 *
 * One run per lesson per job, kept after the lesson changes: Verified points at a run and
 * the content it checked, so a later edit leaves the run readable and the lesson stale.
 */
export const verificationRunIdSchema = idSchema
    .describe('A verification run id.')
    .brand();
/** The steps a verify row reports, in the order it takes them. */
export const verifyCheckSchema = z
    .enum([
    'READING_CONTENT',
    'IDENTIFYING_CLAIMS',
    'ACCESSING_SOURCES',
    'COMPARING_EVIDENCE',
    'CHECKING_QUALITY',
])
    .describe('One check a verification run makes.');
export const checkResultSchema = z
    .strictObject({
    check: verifyCheckSchema,
    completed: z.boolean(),
    reason: z
        .string()
        .min(1)
        .optional()
        .describe('Why it was skipped. Absent on a completed check.'),
})
    .describe('Whether one check ran. A skipped check blocks Verified.');
export const readingDepthSchema = z
    .enum(['FULL_TEXT', 'PASSAGES', 'ABSTRACT', 'METADATA_ONLY'])
    .describe('How much of the source the run held. Only FULL_TEXT and PASSAGES count as read.');
export const isRead = (depth) => depth === 'FULL_TEXT' || depth === 'PASSAGES';
const claimsSchema = z.array(z.string().min(1).max(300));
export const checkedSourceSchema = z
    .strictObject({
    title: z.string().min(1),
    url: z.string().min(1).optional(),
    type: sourceTypeSchema,
    original: z
        .boolean()
        .describe('An original or official source, as opposed to a secondary one.'),
    originalBasis: z
        .string()
        .min(1)
        .describe('Why it was judged original or secondary.'),
    reached: z.boolean().describe('Whether the run got any answer from it.'),
    failure: z
        .string()
        .min(1)
        .optional()
        .describe('Why it was not reached, or why it was not read.'),
    depth: readingDepthSchema,
    supported: claimsSchema.describe('Claims this source supports.'),
    unsupported: claimsSchema.describe('Claims this source was read against and does not support.'),
    unresolved: claimsSchema.describe('Claims this source could not settle, because it was not read or not judged.'),
    limitations: z.array(z.string().min(1)),
    replacement: z
        .string()
        .min(1)
        .optional()
        .describe('A source used in its place, and why. Absent when none was.'),
})
    .describe('One source as a run found it. Reached, read and supports are separate.');
export const guidanceStatusSchema = z
    .enum(['APPLIED', 'NOT_RELEVANT', 'UNAVAILABLE'])
    .describe('Applied by this run, available but not what this run checks, or not available to it.');
export const guidanceSchema = z
    .strictObject({
    name: z.string().min(1),
    digest: z
        .string()
        .min(1)
        .optional()
        .describe('Content hash of the skill file. Absent when there is none.'),
    status: guidanceStatusSchema,
    blocking: z
        .boolean()
        .describe('Whether its absence keeps the lesson from Verified.'),
    affects: z
        .array(verifyCheckSchema)
        .optional()
        .describe('The checks that read it — CURR-31. Absent on runs filed before it was recorded.'),
})
    .describe('One piece of guidance and what this run did with it.');
export const verificationReportSchema = z
    .strictObject({
    scope: z
        .array(z.string().min(1))
        .describe('What was checked: the lesson, its scenes and its sources.'),
    checks: z.array(checkResultSchema),
    sources: z.array(checkedSourceSchema),
    claims: claimsSchema.describe('Every claim the run identified.'),
    unsupported: claimsSchema.describe('Claims a read source contradicts or does not state.'),
    unresolved: claimsSchema.describe('Claims no read source settled.'),
    findings: z
        .array(findingSchema)
        .describe('What the run found. Each suggestion is its proposed fix.'),
    manualReview: z
        .array(z.string().min(1))
        .describe('What a person has to check, because the run could not.'),
    limitations: z.array(z.string().min(1)),
    guidance: z.array(guidanceSchema),
})
    .describe('Everything one run checked and found.');
export const verificationRunSchema = verificationReportSchema
    .extend({
    id: verificationRunIdSchema,
    lessonId: lessonIdSchema,
    jobId: generationJobIdSchema.optional(),
    lessonVersion: z
        .number()
        .int()
        .min(1)
        .describe('The lesson’s published version when it was checked.'),
    fingerprint: z
        .string()
        .min(1)
        .describe('A hash of the lesson’s content and sources as checked. An edit changes it.'),
    passed: z.boolean().describe('Every required check completed and held.'),
    current: z
        .boolean()
        .describe('Whether the lesson still has the content this run checked.'),
    createdAt: z.iso.datetime(),
})
    .describe('One verification run of one lesson.');
export const verificationSummarySchema = verificationRunSchema
    .pick({
    id: true,
    jobId: true,
    lessonVersion: true,
    fingerprint: true,
    passed: true,
    current: true,
    createdAt: true,
})
    .describe('A run as a lesson’s history lists it.');
export const lessonVerificationsSchema = z
    .strictObject({
    verified: z
        .strictObject({
        runId: verificationRunIdSchema,
        lessonVersion: z.number().int().min(1),
    })
        .optional()
        .describe('The run that makes the lesson Verified: the latest run of the lesson as it is now, if that run passed. Absent otherwise.'),
    runs: z.array(verificationSummarySchema).describe('Newest first.'),
})
    .describe('A lesson’s verification runs, and whether it is Verified.');
export const startVerificationSchema = z
    .strictObject({ lessonIds: z.array(lessonIdSchema).min(1).max(60) })
    .describe('The lessons to verify, one run each.');
export const jobVerificationsSchema = z
    .strictObject({
    guidance: z
        .array(guidanceSchema)
        .describe('Each piece of guidance across the job’s runs. Unavailable in any run reads as unavailable; applied in any other, as applied.'),
    runs: z
        .array(verificationSummarySchema.extend({
        lessonId: lessonIdSchema,
        lessonTitle: z.string().min(1),
    }))
        .describe('The runs the job filed, oldest first.'),
})
    .describe('What a job’s verify rows filed — CURR-31.');
/**
 * One source checked on request, with the evidence for each claim — CURR-37. Kept apart
 * from `checkedSourceSchema`, which stored runs are parsed with.
 */
export const sourceCheckStatusSchema = z
    .enum(['UNREACHABLE', 'UNREADABLE', 'UNJUDGED', 'CHECKED'])
    .describe('No answer from the URL; an answer with nothing readable; read but not judged; or read and judged.');
export const claimVerdictSchema = z
    .enum(['SUPPORTED', 'UNSUPPORTED', 'UNRESOLVED'])
    .describe('The source states it; was read and does not; or could not settle it.');
export const citedRefSchema = z.strictObject({
    title: z.string().min(1),
    url: z.string().min(1),
});
export const sourceProposalSchema = z
    .discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('REPAIR_CITATION'),
        cite: citedRefSchema.describe('A source the lesson already cites that states the claim.'),
        reason: z.string().min(1),
    }),
    z.strictObject({
        kind: z.literal('REPLACE_SOURCE'),
        needs: z
            .string()
            .min(1)
            .describe('What a replacement source has to state.'),
        reason: z.string().min(1),
    }),
    z.strictObject({
        kind: z.literal('REVISE_CLAIM'),
        passage: z
            .string()
            .min(1)
            .describe('What the source says instead, to revise the claim to.'),
        reason: z.string().min(1),
    }),
])
    .describe('A change that would settle an unsupported claim. Proposed, never applied here.');
export const claimEvidenceSchema = z
    .strictObject({
    claim: z.string().min(1).max(300),
    scenes: z
        .array(z.number().int().min(1))
        .describe('Positions of the scenes that make the claim, 1-based.'),
    verdict: claimVerdictSchema,
    passage: z
        .string()
        .min(1)
        .optional()
        .describe('The part of the page nearest the claim. Absent when unread.'),
    blocking: z
        .boolean()
        .describe('Whether it keeps the lesson from publishing: docs/decisions/source-findings.md.'),
    reason: z.string().min(1).describe('Why it blocks or only advises.'),
    proposal: sourceProposalSchema.optional(),
})
    .describe('One claim held against one source.');
export const sourceCheckSchema = z
    .strictObject({
    title: z.string().min(1),
    url: z.string().min(1).describe('The exact URL the lesson cites.'),
    finalUrl: z
        .string()
        .min(1)
        .optional()
        .describe('Where redirects ended. Absent when not reached.'),
    pageTitle: z.string().min(1).optional(),
    status: sourceCheckStatusSchema,
    failure: z.string().min(1).optional(),
    depth: readingDepthSchema,
    claims: z.array(claimEvidenceSchema),
    advisories: z
        .array(z.string().min(1))
        .describe('Metadata issues — title, address — that never block.'),
    current: z
        .boolean()
        .describe('Whether the lesson still has the content this check read.'),
    checkedAt: z.iso.datetime(),
})
    .describe('One source a lesson cites, as last checked.');
export const lessonSourceChecksSchema = z
    .strictObject({
    sources: z.array(z.strictObject({
        title: z.string().min(1),
        url: z.string().min(1).optional(),
        type: sourceTypeSchema,
        check: sourceCheckSchema
            .optional()
            .describe('Absent until the source is checked.'),
    })),
})
    .describe('Every source a lesson cites, and its last check.');
export const checkSourcesSchema = z
    .strictObject({
    url: z
        .string()
        .min(1)
        .optional()
        .describe('One cited source. Absent checks every source the lesson cites.'),
    claim: z
        .string()
        .min(1)
        .max(300)
        .optional()
        .describe('One claim. Absent checks the claims of the lesson’s latest verification run.'),
})
    .describe('What to check: a source, a claim, or both.');
//# sourceMappingURL=verification.js.map